// P:\0PJ\synforge\frontend\app\settings\usage\page.tsx v8 2026.07.31 - all $ amounts replaced with Synforge token display (2 decimal places, e.g. "0.93 tokens") using token_value_usd from pricing API; 원가 display already removed in v7

'use client';

import { useState, useEffect } from 'react';
import { billingAPI } from '@/lib/api';
import { useLocale } from '@/contexts/LocaleContext';

type Tab = 'meeting' | 'service';

const SERVICE_LABELS: Record<string, string> = {
  meeting:  'Meeting Session',
  image:    'Image Generation',
  text:     'Text Generation',
  document: 'Document Generation',
  code:     'Code Generation',
};

export default function UsagePage() {
  const { t } = useLocale();
  const [tab, setTab] = useState<Tab>('meeting');

  const [charges, setCharges] = useState<any[]>([]);
  const [summary, setSummary] = useState<{ by_meeting: any[]; by_service: any[] } | null>(null);
  const [loading, setLoading] = useState(true);

  const [expandedId, setExpandedId] = useState<string | null>(null);
  const [detailCache, setDetailCache] = useState<Record<string, any>>({});
  const [detailLoading, setDetailLoading] = useState<string | null>(null);

  // USD -> Synforge 토큰 환산율. 로드 전까지는 0으로 두고(표시는 로딩 대비 아래에서 방어).
  const [tokenValueUsd, setTokenValueUsd] = useState<number | null>(null);

  useEffect(() => {
    setLoading(true);
    Promise.all([billingAPI.getCharges(), billingAPI.getUsageSummary(), billingAPI.getPricing()])
      .then(([chargesRes, summaryRes, pricingRes]) => {
        setCharges(chargesRes.data);
        setSummary(summaryRes.data);
        setTokenValueUsd(pricingRes.data.token_value_usd);
      })
      .catch(() => {})
      .finally(() => setLoading(false));
  }, []);

  // USD 금액을 Synforge 토큰 소수점 표시로 바꾼다. 정수로 반올림하면 소액 건이 0으로 뭉개지므로
  // 소수점 2자리까지 보여준다(실제 차감은 History 쪽에서 정수로 처리됨 - 이 페이지는 표시 전용).
  const usdToTokens = (usd: number): string => {
    if (!tokenValueUsd) return '...';
    return (usd / tokenValueUsd).toFixed(2);
  };

  const totalUsd = charges.reduce((s, c) => s + (c.total_usd || 0), 0);

  const TABS: { id: Tab; label: string }[] = [
    { id: 'meeting', label: t('byMeeting') },
    { id: 'service', label: t('byService') },
  ];

  const toggleExpand = async (sessionId: string | null) => {
    if (!sessionId) return;
    if (expandedId === sessionId) { setExpandedId(null); return; }
    setExpandedId(sessionId);
    if (!detailCache[sessionId]) {
      setDetailLoading(sessionId);
      try {
        const res = await billingAPI.getSessionDetail(sessionId);
        setDetailCache((prev) => ({ ...prev, [sessionId]: res.data }));
      } catch (err) {
        console.error('Failed to load session detail:', err);
      } finally {
        setDetailLoading(null);
      }
    }
  };

  return (
    <div className="p-6 max-w-[640px]">

      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-xl font-semibold text-white">{t('settings.usage.title')}</h1>
          <p className="text-white/30 text-sm mt-0.5">{t('settings.usage.subtitle')}</p>
        </div>
        <div className="text-right">
          <p className="text-lg font-bold text-white">{usdToTokens(totalUsd)} tokens</p>
          <p className="text-[10px] text-white/25">Total charged</p>
        </div>
      </div>

      <div className="flex items-center gap-1 mb-6 bg-white/[0.03] rounded-xl p-1 w-fit">
        {TABS.map((tb) => (
          <button key={tb.id} onClick={() => setTab(tb.id)}
            className={`px-4 py-1.5 rounded-lg text-xs font-medium transition-colors ${
              tab === tb.id ? 'bg-purple-600 text-white' : 'text-white/40 hover:text-white/70'
            }`}>
            {tb.label}
          </button>
        ))}
      </div>

      {loading ? (
        <div className="flex items-center justify-center py-20">
          <div className="animate-spin w-5 h-5 border-2 border-purple-400 border-t-transparent rounded-full" />
        </div>
      ) : (
        <>
          {tab === 'meeting' && (
            <div>
              {!summary || summary.by_meeting.length === 0 ? (
                <p className="text-xs text-white/30">No meeting usage yet.</p>
              ) : summary.by_meeting.map((m, idx) => {
                const detail = m.session_id ? detailCache[m.session_id] : null;
                return (
                  <div key={m.session_id || `no-session-${idx}`} className="border-b border-white/[0.05] last:border-0">
                    <button
                      onClick={() => toggleExpand(m.session_id)}
                      disabled={!m.session_id}
                      className={`w-full flex items-center justify-between py-3 text-left ${m.session_id ? 'cursor-pointer hover:bg-white/[0.02]' : 'cursor-default'}`}
                    >
                      <div>
                        <p className="text-sm text-white">{m.agenda}</p>
                        <p className="text-[11px] text-white/25 mt-0.5">
                          {new Date(m.created_at).toLocaleString()} · {m.count} charge{m.count > 1 ? 's' : ''}
                          {!m.session_id && <span className="ml-2 text-white/20">(no detail available)</span>}
                        </p>
                      </div>
                      <p className="text-sm text-white">{usdToTokens(m.total_usd)} tokens</p>
                    </button>

                    {expandedId === m.session_id && m.session_id && (
                      <div className="pl-4 pb-3 space-y-3">
                        {detailLoading === m.session_id ? (
                          <p className="text-[11px] text-white/25">Loading detail...</p>
                        ) : !detail || (detail.sections || []).length === 0 ? (
                          <p className="text-[11px] text-white/25">No call detail found.</p>
                        ) : detail.sections.map((sec: any) => (
                          <div key={sec.phase || 'unknown'}>
                            <div className="flex items-center justify-between text-xs mb-1">
                              <span className="text-purple-300 font-medium">{sec.phase_label}</span>
                              <span className="text-white/60">{usdToTokens(sec.total_usd)} tokens</span>
                            </div>
                            <div className="pl-2 space-y-1">
                              {sec.items.map((it: any) => (
                                <div key={it.id} className="flex items-center justify-between text-[11px] py-0.5">
                                  <span className="text-white/30">{new Date(it.created_at).toLocaleTimeString()}</span>
                                  <span className="text-white/60" title="과금">{usdToTokens(it.total_usd)} tokens</span>
                                </div>
                              ))}
                            </div>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          )}

          {tab === 'service' && (
            <div className="space-y-4">
              {!summary || summary.by_service.length === 0 ? (
                <p className="text-xs text-white/30">No usage yet.</p>
              ) : summary.by_service.map((s) => {
                const pct = totalUsd > 0 ? Math.round((s.total_usd / totalUsd) * 100) : 0;
                return (
                  <div key={s.service_type}>
                    <div className="flex items-center justify-between text-xs mb-1.5">
                      <span className="text-white/60">{SERVICE_LABELS[s.service_type] || s.service_type}</span>
                      <span className="text-white">{usdToTokens(s.total_usd)} tokens <span className="text-white/25">· {s.count} calls</span></span>
                    </div>
                    <div className="h-1.5 bg-white/[0.06] rounded-full overflow-hidden">
                      <div className="h-full bg-purple-500/60 rounded-full" style={{ width: `${pct}%` }} />
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </>
      )}
    </div>
  );
}